import React, { useEffect, useState } from 'react'
import { UserCog } from 'lucide-react'
import { api } from '@/lib/api'
import { clearImpersonatedSession, getImpersonation } from '@/utils/tokenService'

const formatRemaining = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

/**
 * Always-visible reminder that this tab is acting as someone else, with the
 * time left and an Exit button. Only renders in a tab that holds an
 * impersonated session (sessionStorage flag), so the super admin's own tabs
 * never show it.
 */
const ImpersonationBanner: React.FC = () => {
  const [info] = useState(() => getImpersonation())
  const [remaining, setRemaining] = useState(() =>
    info ? new Date(info.expiresAt).getTime() - Date.now() : 0,
  )
  const [exiting, setExiting] = useState(false)

  const exit = async () => {
    if (exiting) return
    setExiting(true)
    try {
      // Revoke server-side first (needs the impersonated token), then drop it locally.
      await api.post('/impersonation/end')
    } catch {
      // Already expired/revoked — still leave.
    }
    clearImpersonatedSession()
    window.close() // works because the super admin's tab opened this one
    // If the browser refuses to close it, fall back to the super admin's own session.
    window.location.replace('/')
  }

  useEffect(() => {
    if (!info) return
    const timer = window.setInterval(() => {
      setRemaining(new Date(info.expiresAt).getTime() - Date.now())
    }, 1000)
    return () => window.clearInterval(timer)
  }, [info])

  useEffect(() => {
    if (info && remaining <= 0) void exit()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining <= 0])

  if (!info) return null

  return (
    <div
      role="status"
      className="fixed bottom-4 left-1/2 z-[1000] flex max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-3 rounded-full bg-amber-500 px-4 py-2 text-sm font-medium text-white shadow-lg"
    >
      <UserCog className="h-4 w-4 shrink-0" />
      <span className="truncate">
        Viewing as <strong>{info.targetName || 'this user'}</strong> ({info.targetRole.replace('_', ' ')})
      </span>
      <span className="shrink-0 tabular-nums opacity-90">{formatRemaining(remaining)} left</span>
      <button
        type="button"
        onClick={exit}
        disabled={exiting}
        className="shrink-0 rounded-full bg-white/90 px-3 py-0.5 text-xs font-semibold text-amber-700 hover:bg-white disabled:opacity-60"
      >
        {exiting ? 'Exiting…' : 'Exit'}
      </button>
    </div>
  )
}

export default ImpersonationBanner
